import React, { useState, useEffect } from 'react';
import {
    E, T,
    usePortfolio,
    useUser,
    useQuotes,
    wsClient,
    ordersManager
} from '../../logic/imports';
import { isWithinUsMarketHours } from '../../shared/utils';
import { showDimmer, hideDimmer } from '../app/Dimmer';

// Import UserManager to persist settings
import { userManager } from '../../logic/user_data/UserManager';

// Helper function to format date as YYYY-MM-DD in local timezone
function formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

//#region Helper Functions

/**
 * Calculate number of contracts based on position sizing configuration
 * @param positionSizing The active position sizing type and amount
 * @param totalAccountValue Total account equity value
 * @param askPrice The price of the option contract
 */
function calculateContracts(
    positionSizing: T.PositionSizing | undefined,
    totalAccountValue: number,
    askPrice: number
): number {
    if (!positionSizing || !askPrice || askPrice <= 0) return 0;

    const value = positionSizing[positionSizing.activeType];
    if (value <= 0) return 0;

    let targetCost = 0;
    
    if (positionSizing.activeType === 'amount') {
        targetCost = value;
    } else if (positionSizing.activeType === 'percent') {
        targetCost = (value / 100) * totalAccountValue;
    } else if (positionSizing.activeType === 'contracts') {
        return Math.floor(value);
    }

    const contractCost = askPrice * 100; // cost per contract
    return Math.floor(targetCost / contractCost);
}

/**
 * Check if user can afford to buy the given number of contracts
 */
function canAfford(
    contracts: number,
    askPrice: number,
    buyingPower: number
): boolean {
    if (contracts <= 0 || !askPrice) return false;
    const totalCost = contracts * askPrice * 100;
    return totalCost <= buyingPower;
}

/**
 * Get strikes to display: 3 puts (< spyPrice), stock price, 3 calls (>= spyPrice)
 */
function getStrikesToDisplay(
    optionChain: T.OptionChain | null,
    spyPrice: number | null
): { puts: T.Quote[], calls: T.Quote[], valid: boolean } {
    if (!optionChain || !spyPrice || optionChain.calls.length === 0 || optionChain.puts.length === 0) {
        return { puts: [], calls: [], valid: false };
    }

    // Filter and sort puts (< spyPrice), get highest 3 (closest to ATM), then reverse for left-to-right ascending display
    const putQuotes = optionChain.puts
        .filter(q => 'strike' in q.instrument && q.instrument.strike !== undefined && q.instrument.strike! < spyPrice)
        .sort((a, b) => {
            const aStrike = 'strike' in a.instrument ? a.instrument.strike ?? 0 : 0;
            const bStrike = 'strike' in b.instrument ? b.instrument.strike ?? 0 : 0;
            return bStrike - aStrike;
        })
        .slice(0, 3)
        .reverse();

    // Filter and sort calls (>= spyPrice), sorted ascending so lowest are first (closest to ATM)
    const callQuotes = optionChain.calls
        .filter(q => 'strike' in q.instrument && q.instrument.strike !== undefined && q.instrument.strike! >= spyPrice)
        .sort((a, b) => {
            const aStrike = 'strike' in a.instrument ? a.instrument.strike ?? 0 : 0;
            const bStrike = 'strike' in b.instrument ? b.instrument.strike ?? 0 : 0;
            return aStrike - bStrike;
        })
        .slice(0, 3);

    return {
        puts: putQuotes,
        calls: callQuotes,
        valid: putQuotes.length > 0 && callQuotes.length > 0
    };
}

//#endregion

export const BuyPanel: React.FC = () => {
    const { balances, isConnected } = usePortfolio();
    const { userData, userPrefs } = useUser();
    const { quotes, requestQuote, releaseQuote } = useQuotes();
    
    // Pricing state
    const [spyPrice, setSpyPrice] = useState<number | null>(null);
    const [zeroDTE, setZeroDTE] = useState<string | null>(null);
    const [oneDTE, setOneDTE] = useState<string | null>(null);
    const [selectedDTE, setSelectedDTE] = useState<0 | 1>(1);
    const [optionChain, setOptionChain] = useState<T.OptionChain | null>(null);

    const [positionSizing, setPositionSizing] = useState<T.PositionSizing>(
        (userManager.prefs.tradePrefs.positionSizing as T.PositionSizing)
    );

    // Subscribe to SPY quote on mount
    useEffect(() => {
        const subId = crypto.randomUUID().substring(0, 3);
        requestQuote('SPY', `BuyPanel-${subId}`);
        return () => {
            releaseQuote('SPY', `BuyPanel-${subId}`);
        };
    }, []);

    // Update SPY price when quotes change
    useEffect(() => {
        const spyQuote = quotes.find(q => {
            if ('instrument' in q) return q.instrument.symbol === 'SPY';
            return false;
        });
        if (spyQuote?.last) {
            setSpyPrice(spyQuote.last);
        }
    }, [quotes]);

    // Listen for option chain updates from WsClient
    useEffect(() => {
        wsClient.onOptionChainUpdate = (chainData: any) => {
            setOptionChain(chainData);
        };
        return () => {
            wsClient.onOptionChainUpdate = null;
        };
    }, []);

    // Fetch SPY option expiries on mount (only during market hours and when connected)
    useEffect(() => {
        if (!isWithinUsMarketHours() || !isConnected) return;

        const fetchExpiries = async () => {
            try {
                const expiryList = await wsClient.sendRequest(
                    E.WsRoute.QUOTES,
                    E.WsQuoteCommand.GET_EXPIRIES,
                    'SPY'
                );

                if (!expiryList || expiryList.length === 0) {
                    showDimmer('No expiries found for SPY', true);
                    return;
                }

                const today = new Date();
                const todayStr = formatLocalDate(today);
                
                const todayExpiry = expiryList.find((exp: string) => exp === todayStr);
                if (todayExpiry) setZeroDTE(todayExpiry);
                
                const futureExpiries = expiryList.filter((exp: string) => exp > todayStr);
                if (futureExpiries.length > 0) {
                    const nextExpiry = futureExpiries.sort()[0];
                    setOneDTE(nextExpiry);
                }
            } catch (error) {
                console.error('[BuyPanel] Failed to fetch expiries:', error);
            }
        };

        fetchExpiries();
    }, [isConnected]);

    // Get the selected expiry based on DTE selection
    const selectedExpiry = selectedDTE === 0 ? zeroDTE : oneDTE;

    // Load option chain when expiry changes (only when connected)
    useEffect(() => {
        if (!isWithinUsMarketHours() || !selectedExpiry || !isConnected) {
            setOptionChain(null);
            return;
        }

        const loadOptionChain = async () => {
            setOptionChain(null);
            try {
                // TODO have server verify selected chain exists and respond
                await wsClient.sendWsMessage(
                    E.WsRoute.QUOTES,
                    E.WsQuoteCommand.STREAM_CHAIN,
                    { symbol: 'SPY', expiry: selectedExpiry }
                );
            } catch (error) {
                console.error('[BuyPanel] Failed to load option chain:', error);
            }
        };

        loadOptionChain();

        return () => {
            wsClient.sendWsMessage(E.WsRoute.QUOTES, E.WsQuoteCommand.UNSTREAM_CHAIN);
        };
    }, [selectedExpiry, isConnected]);

    // Get strikes to display
    const strikeDisplay = getStrikesToDisplay(optionChain, spyPrice);

    // Persist position sizing to user preferences
    useEffect(() => {
        if (userPrefs) {
            // Update in-memory preferences with new position sizing
            userManager.prefs.tradePrefs.positionSizing = positionSizing;
            userManager.onUserUpdated?.(userManager.data, userManager.prefs);
            
            // Persist to server via UserManager
            userManager.saveUserData({ tradePrefs: true }).catch((error) => {
                console.error('[BuyPanel] Failed to persist trade preferences:', error);
            });
        }
    }, [positionSizing]);

    // Handle position sizing value change
    const handlePositionSizingChange = (type: 'amount' | 'percent' | 'contracts', value: number) => {
        setPositionSizing(prev => ({
            ...prev,
            [type]: value
        }));
    };

    // Handle position sizing type selection
    const handlePositionSizingTypeClick = (type: 'amount' | 'percent' | 'contracts') => {
        setPositionSizing(prev => ({
            ...prev,
            activeType: type
        }));
    };

    // Handle input blur to normalize displayed value (remove leading zeros)
    const handlePositionSizingBlur = (type: 'amount' | 'percent' | 'contracts') => {
        return (e: React.FocusEvent<HTMLInputElement>) => {
            const normalized = parseFloat(e.target.value) || 0;
            handlePositionSizingChange(type, normalized);
            e.target.value = normalized.toString();
        };
    };

    // Handle DTE button click
    const handleDTEClick = (dte: 0 | 1) => {
        setSelectedDTE(dte);
    };

    // Handle option buy click
    const handleOptionClick = async (quote: T.Quote) => {
        if (!quote) return;

        const totalAccountValue = balances?.accountValue ?? 0;
        const contracts = calculateContracts(positionSizing, totalAccountValue, quote.ask);

        if (contracts <= 0) {
            showDimmer('Invalid contract quantity', true);
            return;
        }

        if (!canAfford(contracts, quote.ask, balances?.cashSettled ?? 0)) {
            showDimmer('Insufficient buying power', true);
            return;
        }

        const order: any = {
            id: crypto.randomUUID(),
            brokerageId: undefined,
            instrument: quote.instrument,
            status: E.OrderStatus.WORKING,
            side: E.OrderSide.BUY,
            type: E.OrderType.MARKET,
            quantity: contracts,
            isManaged: false,
            timeInForce: E.TimeInForce.DAY
        };

        try {
            showDimmer('Placing order...', false);
            // Send order request
            console.log('[BuyPanel] Placing order:', order);
            await ordersManager.placeOrder(order);
            console.log('[BuyPanel] Order placed successfully');
            hideDimmer();
        } catch (error: any) {
            console.error('[BuyPanel] Order failed:', error);
            showDimmer('Order failed: ' + error.message, true);
        }
    };

    // Determine if strike buttons should be disabled
    const getStrikeButtonDisabledReason = (quote: T.Quote): string | undefined => {
        if (!isWithinUsMarketHours()) return 'Market closed';
        if (!strikeDisplay.valid) return 'Loading...';
        
        const totalAccountValue = (balances?.accountValue ?? 0);
        const contracts = calculateContracts(positionSizing, totalAccountValue, quote.ask);
        
        if (contracts <= 0) return 'Invalid sizing';
        if (!canAfford(contracts, quote.ask, balances?.cashSettled ?? 0)) return 'Insufficient BP';
        if (quote.bid < 0.1) return 'Bid too low';
        return undefined;
    };

    const renderSettings = () => {
        return (
            <div style={{ marginBottom: '20px' }}>
                {/* Row 1: Position Sizing */}
                <div style={{ marginBottom: '20px' }}>

                    {/* Position Sizing Label */}
                    <div style={{
                        fontWeight: 'bold',
                        marginBottom: '8px',
                        color: '#aaa'
                    }}>
                        Position Sizing
                    </div>

                    {/* Sizing Options Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                        {/* $ Amount Button */}
                        <button
                            className={`sizing-button${positionSizing.activeType === 'amount' ? ' sizing-button--active' : ''}`}
                            onClick={() => handlePositionSizingTypeClick('amount')}
                        >
                            <p>$ Amount</p>
                            <input
                                type="number"
                                value={positionSizing.amount.toString()}
                                onChange={(e) => handlePositionSizingChange('amount', Math.max(0, Math.min(999999, parseFloat(e.target.value) || 0)))}
                                onBlur={handlePositionSizingBlur('amount')}
                                onClick={(e) => e.stopPropagation()}
                                min="0"
                                max="999999"
                            />
                        </button>

                        {/* % Account Button */}
                        <button
                            className={`sizing-button${positionSizing.activeType === 'percent' ? ' sizing-button--active' : ''}`}
                            onClick={() => handlePositionSizingTypeClick('percent')}
                        >
                            <p>% Account</p>
                            <input
                                type="number"
                                value={positionSizing.percent.toString()}
                                onChange={(e) => handlePositionSizingChange('percent', Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                                onBlur={handlePositionSizingBlur('percent')}
                                onClick={(e) => e.stopPropagation()}
                                min="0"
                                max="100"
                            />
                        </button>

                        {/* # Contracts Button */}
                        <button
                            className={`sizing-button${positionSizing.activeType === 'contracts' ? ' sizing-button--active' : ''}`}
                            onClick={() => handlePositionSizingTypeClick('contracts')}
                        >
                            <p># Contracts</p>
                            <input
                                type="number"
                                value={positionSizing.contracts.toString()}
                                onChange={(e) => handlePositionSizingChange('contracts', Math.max(0, Math.min(999999, parseFloat(e.target.value) || 0)))}
                                onBlur={handlePositionSizingBlur('contracts')}
                                onClick={(e) => e.stopPropagation()}
                                min="0"
                                max="999999"
                            />
                        </button>
                    </div>
                </div>

                {/* Row 2: Days to Expiry */}
                <div>
                    {/* Days to Expiry Label */}
                    <div style={{
                        fontWeight: 'bold',
                        marginBottom: '8px',
                        color: '#aaa'
                    }}>
                        Days to Expiry
                    </div>
                    {/* Days to Expiry Buttons */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                        <button
                            className={`strike-limit-button${selectedDTE === 0 ? ' strike-limit-button--active' : ''}`}
                            onClick={() => handleDTEClick(0)}
                            disabled={!zeroDTE}
                            style={{
                                borderRadius: '4px',
                                opacity: !zeroDTE ? 0.5 : 1,
                                cursor: !zeroDTE ? 'not-allowed' : 'pointer',
                                height: '35px'
                            }}
                        >
                            0 DTE
                        </button>

                        <button
                            className={`strike-limit-button${selectedDTE === 1 ? ' strike-limit-button--active' : ''}`}
                            onClick={() => handleDTEClick(1)}
                            disabled={!oneDTE}
                            style={{
                                borderRadius: '4px',
                                opacity: !oneDTE ? 0.5 : 1,
                                cursor: !oneDTE ? 'not-allowed' : 'pointer',
                                height: '35px'
                            }}
                        >
                            1 DTE
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const renderStrikes = () => {
        return (
            <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    gap: '10px',
                    alignItems: 'center'
                }}>
                    {/* Puts Section */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                        {strikeDisplay.puts.map((quote) => {
                            const totalAccountValue = balances?.accountValue ?? 0;
                            const contracts = calculateContracts(positionSizing, totalAccountValue, quote.ask);
                            const disabled = getStrikeButtonDisabledReason(quote);

                            return (
                                <button
                                    key={quote.instrument.symbol}
                                    className={`contract-buy-button contract-buy-button--put`}
                                    onClick={() => handleOptionClick(quote)}
                                    disabled={!!disabled}
                                    title={disabled || ''}
                                >
                                    <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                                        {quote.instrument.kind === 'option' ?
                                            quote.instrument.strike : '?'} P
                                    </div>
                                    <div style={{ fontSize: '0.8em', opacity: 0.8 }}>
                                        {contracts} @ ${quote.ask.toFixed(2)}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Stock Price Center */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        whiteSpace: 'nowrap'
                    }}>
                        {/* Quote */}
                        <div style={{
                            fontSize: '1.5em',
                            fontWeight: 'bold',
                            color: '#59a7ffff',
                            textAlign: 'center'
                        }}>
                            {spyPrice?.toFixed(2) || '...'}
                        </div>
                        {/* Label */}
                        <p style={{
                            fontSize: '0.8em',
                            color: '#aaa',
                            marginTop: '4px',
                        }}>
                            SPY Price
                        </p>
                    </div>

                    {/* Calls Section */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                        {strikeDisplay.calls.map((quote) => {
                            const totalAccountValue = balances?.accountValue ?? 0;
                            const contracts = calculateContracts(positionSizing, totalAccountValue, quote.ask);
                            const disabled = getStrikeButtonDisabledReason(quote);

                            return (
                                <button
                                    key={quote.instrument.symbol}
                                    className={`contract-buy-button contract-buy-button--call`}
                                    onClick={() => handleOptionClick(quote)}
                                    disabled={!!disabled}
                                    title={disabled || ''}
                                >
                                    <div style={{ fontWeight: 'bold' }}>
                                        {quote.instrument.kind === 'option' ? quote.instrument.strike : '?'} C
                                    </div>
                                    <div style={{ fontSize: '0.8em', opacity: 0.8 }}>
                                        {contracts} @ ${quote.ask.toFixed(2)}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
        );
    };

    return (
        <div className="panel">
            <div className="panel-header">
                Buy to Open
            </div>

            {/* Settings Area */}
            {renderSettings()}

            {/* Strikes Header */}
            <div style={{
                fontWeight: 'bold',
                marginBottom: '10px',
                color: '#aaa'
            }}>
                Submit Market Order
            </div>

            {/* Strikes Purchase Buttons */}
            {strikeDisplay.valid ? (
                renderStrikes()
            ) : (
                <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#666',
                    height: '100px'
                }}>
                    {!isWithinUsMarketHours() ? 'Market is closed' : 'Loading strikes...'}
                </div>
            )}

        </div>
    );
}